import { ChevronDown, Target } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { metaAdsFetch, type MetaAdsFetchResult } from 'sprites-types/workflow/tools/metaAdsFetch'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { MetaAdsItem } from './MetaAdsItem'

function MetaAdsFetchDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(metaAdsFetch, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Handle both emitMode: 'single' (array of ads) and 'multi' (array of arrays of ads)
  const ads: MetaAdsFetchResult = Array.isArray(result[0])
    ? (result as MetaAdsFetchResult[]).flat()
    : (result as MetaAdsFetchResult)

  if (ads.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Target className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No Meta ads found</div>
        </div>
      </div>
    )
  }

  if (ads.length === 1) {
    return <MetaAdsItem ad={ads[0]} />
  }

  return (
    <div className="space-y-3">
      {/* Header with context menu and collapse/expand */}
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Target className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Meta Ads</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {ads.length} ads retrieved
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual ads - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {ads.map((ad) => (
            <MetaAdsItem key={ad.ad_archive_id} ad={ad} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadMetaAdsFetch(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function metaAdsFetchToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(metaAdsFetch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  // Handle both emitMode: 'single' (array of ads) and 'multi' (array of arrays of ads)
  const ads: MetaAdsFetchResult = Array.isArray(result[0])
    ? (result as MetaAdsFetchResult[]).flat()
    : (result as MetaAdsFetchResult)

  if (ads.length === 0) {
    return `## Meta Ads

No ads found.

---
`
  }

  const adsMarkdown = ads
    .map((ad) => {
      const { snapshot } = ad
      const startDate = new Date(ad.start_date * 1000)
      const endDate = new Date(ad.end_date * 1000)
      const dateLabel = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`

      return `### ${snapshot.title || 'Meta Ad'}

**Date Range:** ${dateLabel}
**Page:** ${snapshot.page_entity_type}
**Categories:** ${snapshot.page_categories.join(', ')}
**Page Likes:** ${(snapshot.page_like_count ?? 0).toLocaleString()}

**Content:**
${snapshot.body.text}

${snapshot.caption ? `**Caption:** ${snapshot.caption}` : ''}
${snapshot.images && snapshot.images.length > 0 ? `**Images:** ${snapshot.images.length}` : ''}
${snapshot.videos && snapshot.videos.length > 0 ? `**Videos:** ${snapshot.videos.length}` : ''}
${snapshot.cards && snapshot.cards.length > 0 ? `**Cards:** ${snapshot.cards.length}` : ''}
${snapshot.link_url ? `**Link:** ${snapshot.link_url}` : ''}
**Ad Library:** https://www.facebook.com/ads/library/?id=${ad.ad_archive_id}
`
    })
    .join('\n---\n\n')

  return `## Meta Ads (${ads.length} ads)

${adsMarkdown}

---
`
}

export const MetaAdsFetchDisplay = createWorkflowToolComponent(
  MetaAdsFetchDisplayComponent,
  downloadMetaAdsFetch,
  metaAdsFetchToMarkdown
)
